import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useOutletContext, useParams } from 'react-router';
import type { MembershipSummary } from '../../../src/shared/auth';
import type {
  Conversation,
  Message,
  MessageSendBody,
  ConversationStatus,
} from '../../../src/shared/comms';
import {
  commsKeys,
  fetchConversations,
  fetchConversation,
  fetchMessages,
  sendMessage,
  assignConversation,
  closeConversation,
} from '../../api/comms';
import { ErrorNotice } from '../../components/ErrorNotice';
import { LoadingScreen } from '../../components/LoadingScreen';
import { useRealtime } from '../../realtime/useRealtime';

const STATUS_OPTIONS: { label: string; value: ConversationStatus | 'all' }[] = [
  { label: 'All', value: 'all' },
  { label: 'Open', value: 'open' },
  { label: 'Closed', value: 'closed' },
];

function formatTimestamp(epoch: number): string {
  return new Date(epoch * 1000).toLocaleString();
}

function ConversationListItem({
  conversation,
  isActive,
  onClick,
}: {
  conversation: Conversation;
  isActive: boolean;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded-lg border px-4 py-3 text-left text-sm transition-colors ${
        isActive
          ? 'border-slate-900 bg-slate-50'
          : 'border-slate-200 bg-white hover:border-slate-300'
      }`}
      data-testid={`conversation-item-${conversation.id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="truncate font-medium text-slate-900">{conversation.contactId}</span>
        <span
          className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${
            conversation.status === 'open'
              ? 'bg-green-100 text-green-700'
              : 'bg-slate-100 text-slate-600'
          }`}
        >
          {conversation.status}
        </span>
      </div>
      {conversation.assignedUserId !== null && (
        <p className="mt-1 text-xs text-slate-500">Assigned to: {conversation.assignedUserId}</p>
      )}
      {conversation.lastMessageAt !== null && (
        <p className="mt-0.5 text-xs text-slate-400">
          {formatTimestamp(conversation.lastMessageAt)}
        </p>
      )}
    </button>
  );
}

function MessageBubble({ message }: { message: Message }): React.JSX.Element {
  const isInbound = message.direction === 'inbound';
  return (
    <div
      className={`flex ${isInbound ? 'justify-start' : 'justify-end'}`}
      data-testid={`message-${message.id}`}
    >
      <div
        className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${
          isInbound
            ? 'rounded-bl-none border border-slate-200 bg-white text-slate-900'
            : 'rounded-br-none bg-slate-900 text-white'
        }`}
      >
        <p className="whitespace-pre-wrap break-words">{message.body}</p>
        <div className="mt-1 flex items-center gap-2">
          <span className={`text-xs ${isInbound ? 'text-slate-400' : 'text-slate-300'}`}>
            {formatTimestamp(message.createdAt)}
          </span>
          {message.status === 'failed' && (
            <span
              className="text-xs font-medium text-red-500"
              data-testid={`message-status-${message.id}`}
            >
              failed
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function ThreadPane({
  slug,
  conversationId,
  onClose,
}: {
  slug: string;
  conversationId: string;
  onClose: () => void;
}): React.JSX.Element {
  const queryClient = useQueryClient();

  const conversationQuery = useQuery({
    queryKey: commsKeys.conversations.detail(slug, conversationId),
    queryFn: ({ signal }) => fetchConversation(slug, conversationId, signal),
  });

  const messagesQuery = useQuery({
    queryKey: commsKeys.messages.list(slug, conversationId),
    queryFn: ({ signal }) => fetchMessages(slug, conversationId, undefined, signal),
  });

  const [newMessage, setNewMessage] = useState('');

  const sendMutation = useMutation({
    mutationFn: (body: MessageSendBody) => sendMessage(slug, conversationId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: commsKeys.messages.list(slug, conversationId),
      });
      void queryClient.invalidateQueries({ queryKey: commsKeys.conversations.all(slug) });
      setNewMessage('');
    },
  });

  const assignMutation = useMutation({
    mutationFn: () => assignConversation(slug, conversationId, { assignedUserId: '' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: commsKeys.conversations.detail(slug, conversationId),
      });
      void queryClient.invalidateQueries({ queryKey: commsKeys.conversations.all(slug) });
    },
  });

  const closeMutation = useMutation({
    mutationFn: () => closeConversation(slug, conversationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: commsKeys.conversations.all(slug) });
      onClose();
    },
  });

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (newMessage.trim().length === 0) return;
    sendMutation.mutate({ body: newMessage.trim() });
  };

  return (
    <div className="flex h-full flex-col rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
        <h3 className="text-sm font-semibold text-slate-900" data-testid="thread-header">
          {conversationQuery.data?.contactId ?? 'Conversation'}
        </h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => assignMutation.mutate()}
            disabled={assignMutation.isPending}
            className="rounded-md border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="assign-btn"
          >
            {assignMutation.isPending ? 'Assigning…' : 'Assign'}
          </button>
          <button
            type="button"
            onClick={() => closeMutation.mutate()}
            disabled={closeMutation.isPending}
            className="rounded-md border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="close-btn"
          >
            {closeMutation.isPending ? 'Closing…' : 'Close'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-2.5 py-1 text-xs font-medium text-slate-500 hover:text-slate-900"
            data-testid="back-btn"
          >
            Back
          </button>
        </div>
      </div>

      {conversationQuery.isError && (
        <div className="px-4 py-2">
          <ErrorNotice error={conversationQuery.error} />
        </div>
      )}

      <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
        {messagesQuery.isPending && <LoadingScreen />}

        {messagesQuery.isError && <ErrorNotice error={messagesQuery.error} />}

        {messagesQuery.isSuccess && messagesQuery.data.items.length === 0 && (
          <p className="py-8 text-center text-sm text-slate-500" data-testid="messages-empty">
            No messages yet.
          </p>
        )}

        {messagesQuery.isSuccess &&
          messagesQuery.data.items.map((msg) => <MessageBubble key={msg.id} message={msg} />)}
      </div>

      <form onSubmit={handleSend} className="border-t border-slate-200 px-4 py-3">
        <div className="flex gap-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message…"
            disabled={sendMutation.isPending}
            className="min-w-0 flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm placeholder-slate-400 focus:border-slate-900 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="message-input"
          />
          <button
            type="submit"
            disabled={newMessage.trim().length === 0 || sendMutation.isPending}
            className="shrink-0 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="send-btn"
          >
            {sendMutation.isPending ? 'Sending…' : 'Send'}
          </button>
        </div>
        {sendMutation.isError && (
          <div className="mt-2">
            <ErrorNotice error={sendMutation.error} />
          </div>
        )}
      </form>
    </div>
  );
}

export function InboxPage(): React.JSX.Element {
  const { slug } = useParams<'slug'>();
  const { membership } = useOutletContext<{ membership: MembershipSummary }>();

  const [statusFilter, setStatusFilter] = useState<ConversationStatus | 'all'>('all');
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);

  const conversationsQuery = useQuery({
    queryKey: commsKeys.conversations.list(
      slug!,
      statusFilter === 'all' ? undefined : statusFilter
    ),
    queryFn: ({ signal }) =>
      fetchConversations(slug!, statusFilter === 'all' ? {} : { status: statusFilter }, signal),
    enabled: slug !== undefined,
  });

  useRealtime(slug!);

  if (slug === undefined) {
    return <p className="text-sm text-slate-500">Workspace slug not found.</p>;
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight">Inbox</h1>
      <p className="mt-1 text-sm text-slate-500">{membership.name}</p>

      <div
        className="mt-4 flex flex-wrap gap-2"
        role="group"
        aria-label="Conversation status filter"
      >
        {STATUS_OPTIONS.map((opt) => (
          <button
            key={opt.label}
            type="button"
            onClick={() => setStatusFilter(opt.value)}
            className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
              statusFilter === opt.value
                ? 'border-slate-900 bg-slate-900 text-white'
                : 'border-slate-300 text-slate-700 hover:bg-slate-100'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {conversationsQuery.isError && (
        <div className="mt-4">
          <ErrorNotice error={conversationsQuery.error} />
        </div>
      )}

      <div className="mt-4 grid gap-4 lg:grid-cols-[320px_1fr]">
        <div className="space-y-2">
          {conversationsQuery.isPending && <LoadingScreen />}

          {conversationsQuery.isSuccess && conversationsQuery.data.items.length === 0 && (
            <div className="rounded-lg border border-slate-200 bg-white p-8 text-center">
              <p className="text-sm text-slate-500" data-testid="inbox-empty">
                {statusFilter !== 'all'
                  ? `No ${statusFilter} conversations found.`
                  : 'No conversations yet.'}
              </p>
            </div>
          )}

          {conversationsQuery.isSuccess &&
            conversationsQuery.data.items.map((conv) => (
              <ConversationListItem
                key={conv.id}
                conversation={conv}
                isActive={activeConversationId === conv.id}
                onClick={() => setActiveConversationId(conv.id)}
              />
            ))}
        </div>

        <div>
          {activeConversationId !== null ? (
            <ThreadPane
              slug={slug}
              conversationId={activeConversationId}
              onClose={() => setActiveConversationId(null)}
            />
          ) : (
            <div className="flex h-full min-h-[300px] items-center justify-center rounded-lg border border-slate-200 bg-white shadow-sm">
              <p className="text-sm text-slate-500">Select a conversation to view its thread.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
